/**
 * Checklist Issues Component
 *
 * View of all amber/red issues from checklists, grouped by assignee.
 */

import React, { useState, useEffect, useCallback } from "react";
import { useOsNavigate, useIsOsMode } from "@/components/ui/os-layout";
import { OpsSubpageHeadingClassic } from "@/components/ops/OpsSubpageHeadingClassic";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleNotch,
  faRefresh,
  faExclamationTriangle,
  faCheck,
  faFilter,
  faCalendar,
  faUser,
  faEye,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { Badge } from "@/components/ui/badge/Badge";
import { useNotification } from "@/context/NotificationContext";
import {
  getIssues,
  type IssueItem,
  type IssuesResponse,
  OPS_TEAM_MEMBERS,
} from "@/services/api/opsChecklists";

interface IssueCardProps {
  issue: IssueItem;
  onViewRun: (runId: string) => void;
}

function IssueCard({ issue, onViewRun }: IssueCardProps) {
  const isRed = issue.status === "red";
  const isResolved = issue.run_status === "approved";

  return (
    <div
      className={`rounded-lg border p-4 ${
        isResolved
          ? "bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 opacity-60"
          : isRed
            ? "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800"
            : "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800"
      }`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <div
            className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
              isRed ? "bg-red-500" : "bg-amber-500"
            }`}
          />
          <span className="font-medium text-slate-900 dark:text-white text-sm">
            {issue.step_title}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {isResolved && (
            <Badge color="green" className="text-xs">
              <FontAwesomeIcon icon={faCheck} className="mr-1" />
              Resolved
            </Badge>
          )}
          <Badge color={isRed ? "red" : "amber"} className="text-xs">
            {isRed ? "Red" : "Amber"}
          </Badge>
        </div>
      </div>

      <div className="text-xs text-slate-500 dark:text-slate-400 mb-2">
        {issue.checklist_name} • Step {issue.step_order}
      </div>

      <div className="text-sm text-slate-700 dark:text-slate-300 mb-3 whitespace-pre-wrap line-clamp-3">
        {issue.notes}
      </div>

      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-3 text-slate-500 dark:text-slate-400">
          {issue.assigned_to && (
            <span>
              <FontAwesomeIcon icon={faUser} className="mr-1" />
              {issue.assigned_to}
            </span>
          )}
          <span>
            <FontAwesomeIcon icon={faCalendar} className="mr-1" />
            {issue.date}
          </span>
          <span>by {issue.primary_user}</span>
        </div>
        <button
          onClick={() => onViewRun(issue.run_id)}
          className="text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1"
        >
          <FontAwesomeIcon icon={faEye} />
          View
        </button>
      </div>
    </div>
  );
}

export function ChecklistIssues() {
  const isOsMode = useIsOsMode();
  const osNavigate = useOsNavigate();
  const { addNotification } = useNotification();

  const [issues, setIssues] = useState<IssuesResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Filters
  const [showHistorical, setShowHistorical] = useState(false);
  const [filterAssignee, setFilterAssignee] = useState<string>("");
  const [filterStatus, setFilterStatus] = useState<"" | "amber" | "red">("");

  const loadIssues = useCallback(
    async (showRefreshState = false) => {
      try {
        if (showRefreshState) setIsRefreshing(true);

        // If showing historical, get last 7 days
        const today = new Date().toISOString().split("T")[0];
        const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0];

        const data = await getIssues({
          date_from: showHistorical ? weekAgo : today,
          date_to: today,
          assigned_to: filterAssignee || undefined,
          status: filterStatus || undefined,
        });
        setIssues(data);
      } catch (error) {
        console.error("Failed to load issues:", error);
        addNotification({
          type: "error",
          message: "Failed to load issues",
        });
      } finally {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    },
    [addNotification, showHistorical, filterAssignee, filterStatus]
  );

  useEffect(() => {
    loadIssues();
  }, [loadIssues]);

  const handleViewRun = (runId: string) => {
    osNavigate(`/ops/checklists/run/${runId}`, "Checklist Run");
  };

  // Group issues by assignee
  const groupedByAssignee = issues?.issues.reduce(
    (acc, issue) => {
      const key = issue.assigned_to || "Unassigned";
      if (!acc[key]) acc[key] = [];
      acc[key].push(issue);
      return acc;
    },
    {} as Record<string, IssueItem[]>
  );

  // Sort assignees: unassigned first, then alphabetically
  const sortedAssignees = groupedByAssignee
    ? Object.keys(groupedByAssignee).sort((a, b) => {
        if (a === "Unassigned") return -1;
        if (b === "Unassigned") return 1;
        return a.localeCompare(b);
      })
    : [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <FontAwesomeIcon
          icon={faCircleNotch}
          className="text-2xl text-indigo-500 animate-spin"
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-shrink-0 gap-4">
        <OpsSubpageHeadingClassic
          title="Issues"
          titleClassName="text-xl font-bold text-slate-900 dark:text-white"
          backTo="/ops/checklists"
          hubWindowTitle="Ops Checklists"
          useOsLink
          linkClassName="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
          beforeTitle={
            !isOsMode ? (
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="text-amber-500 text-xl"
              />
            ) : undefined
          }
          subtitle={
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {showHistorical ? "Last 7 days" : "Today"} •{" "}
              {issues?.stats.total || 0} issue
              {issues?.stats.total !== 1 ? "s" : ""}
            </p>
          }
        />

        <button
          onClick={() => loadIssues(true)}
          disabled={isRefreshing}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
        >
          <FontAwesomeIcon
            icon={isRefreshing ? faCircleNotch : faRefresh}
            className={isRefreshing ? "animate-spin" : ""}
          />
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-3 mb-6 flex-shrink-0">
        <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-slate-700 dark:text-slate-300">
            {issues?.stats.total || 0}
          </div>
          <div className="text-xs text-slate-500">Total</div>
        </div>
        <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-red-600 dark:text-red-400">
            {issues?.stats.red || 0}
          </div>
          <div className="text-xs text-red-700 dark:text-red-500">Red</div>
        </div>
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">
            {issues?.stats.amber || 0}
          </div>
          <div className="text-xs text-amber-700 dark:text-amber-500">Amber</div>
        </div>
        <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-slate-600 dark:text-slate-400">
            {issues?.stats.unresolved || 0}
          </div>
          <div className="text-xs text-slate-500">Unresolved</div>
        </div>
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">
            {issues?.stats.resolved || 0}
          </div>
          <div className="text-xs text-green-700 dark:text-green-500">Resolved</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-6 flex-shrink-0 flex-wrap">
        <div className="flex items-center gap-2">
          <FontAwesomeIcon
            icon={faFilter}
            className="text-slate-400 text-sm"
          />
          <span className="text-sm text-slate-500 dark:text-slate-400">
            Filters:
          </span>
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showHistorical}
            onChange={(e) => setShowHistorical(e.target.checked)}
            className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
          />
          <span className="text-sm text-slate-700 dark:text-slate-300">
            Show last 7 days
          </span>
        </label>

        <select
          value={filterAssignee}
          onChange={(e) => setFilterAssignee(e.target.value)}
          className="px-3 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
        >
          <option value="">All assignees</option>
          {OPS_TEAM_MEMBERS.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>

        <select
          value={filterStatus}
          onChange={(e) =>
            setFilterStatus(e.target.value as "" | "amber" | "red")
          }
          className="px-3 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
        >
          <option value="">All statuses</option>
          <option value="red">Red only</option>
          <option value="amber">Amber only</option>
        </select>
      </div>

      {/* Issues list */}
      <div className="flex-1 min-h-0 overflow-auto">
        {issues?.issues.length === 0 ? (
          <div className="text-center py-12">
            <FontAwesomeIcon
              icon={faCheck}
              className="text-4xl text-green-400 mb-3"
            />
            <p className="text-slate-500 dark:text-slate-400">
              No issues found. All clear!
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {sortedAssignees.map((assignee) => (
              <div key={assignee}>
                <div className="flex items-center gap-2 mb-3">
                  <FontAwesomeIcon
                    icon={faUser}
                    className="text-slate-400"
                  />
                  <h2 className="font-medium text-slate-900 dark:text-white">
                    {assignee}
                  </h2>
                  <span className="text-sm text-slate-500">
                    ({groupedByAssignee![assignee].length})
                  </span>
                </div>
                <div className="space-y-3 pl-6">
                  {groupedByAssignee![assignee].map((issue) => (
                    <IssueCard
                      key={`${issue.run_id}-${issue.step_id}`}
                      issue={issue}
                      onViewRun={handleViewRun}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
